import { useEffect, useRef, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
  useActiveTabId,
  useActiveUi,
  useTabReplay,
} from "@/state/hooks";
import {
  cancelReplaySelect,
  exitReplay,
  startReplaySelect,
} from "@/state/slices/replaySlice";
import {
  clearAllDrawings,
  setDrawingTool,
  setFibLevelsDialogOpen,
  setSettingsToolId,
  type DrawingTool,
} from "@/state/slices/uiSlice";
import { FibLevelsDialog } from "@/components/FibLevelsDialog/FibLevelsDialog";
import { DrawingsDialog } from "@/components/DrawingsDialog/DrawingsDialog";
import { PositionDialog } from "@/components/PositionDialog/PositionDialog";
import { IndicatorsPanel } from "@/indicators/IndicatorsPanel";
import { ToolSettings } from "@/tools/_shared/ToolSettings";
import { toolById } from "@/tools/_shared/registry";
import styles from "./Toolbar.module.css";

// Line-tool settings (vertical / horizontal / trend / Elliott wave) now come
// from the tools registry via ToolSettings — the legacy TOOL_STYLE_KEYS map +
// LineStyleDialog were removed. A tool is "configurable" if the registry has
// a settings block for it (or it's Fibonacci, which keeps its own dialog).
const toolHasSettings = (t: Exclude<DrawingTool, null>): boolean =>
  t === "fibonacci" || toolById(t)?.settings != null;

// SVG icons in the same visual idiom as TradingView's tool rail:
// 28×28 viewBox, lines drawn as 1 px-tall `<rect>` fills (crisper at
// small sizes than strokes), endpoints as hollow `<circle>` anchor
// rings. Rendering size 20 px. Each icon was redrawn from scratch
// using rect + circle primitives — no copied path strings.

// Fibonacci retracement — four stacked horizontal level lines with
// two anchor rings at the diagonal corners (top-left and bottom-
// right of the conceptual trend line).
const FibonacciIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 28 28"
    width="28"
    height="28"
    aria-hidden
  >
    <rect x="3" y="4" width="22" height="1" fill="currentColor" />
    <rect x="6" y="10" width="19" height="1" fill="currentColor" />
    <rect x="3" y="16" width="19" height="1" fill="currentColor" />
    <rect x="3" y="22" width="22" height="1" fill="currentColor" />
    <circle
      cx="24.5"
      cy="10.5"
      r="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    />
    <circle
      cx="3.5"
      cy="16.5"
      r="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    />
  </svg>
);

// Horizontal line tool — 1 px line spanning full width with hollow
// anchor rings at each end.
const HorizontalIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 28 28"
    width="28"
    height="28"
    aria-hidden
  >
    <rect x="3" y="13.5" width="22" height="1" fill="currentColor" />
    <circle
      cx="3.5"
      cy="14"
      r="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    />
    <circle
      cx="24.5"
      cy="14"
      r="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    />
  </svg>
);

// Vertical line tool — same as horizontal, rotated.
const VerticalIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 28 28"
    width="28"
    height="28"
    aria-hidden
  >
    <rect x="13.5" y="3" width="1" height="22" fill="currentColor" />
    <circle
      cx="14"
      cy="3.5"
      r="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    />
    <circle
      cx="14"
      cy="24.5"
      r="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    />
  </svg>
);

// Arrow — multi-segment line that ends in an arrowhead. Drawn in
// blue to match the on-chart rendering so the toolbar icon and the
// committed drawing share a visual identity.
const PathIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 28 28"
    width="28"
    height="28"
    aria-hidden
  >
    {/* Trajectory: bottom-left → mid → top-right, ending in a
        small triangular arrowhead. Stroke + fill both pinned to
        blue (independent of `currentColor`) since "arrow drawing
        is blue" is part of the tool's brand. */}
    <polyline
      points="4,22 12,12 22,6"
      stroke="#2962FF"
      strokeWidth="1.8"
      fill="none"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
    <polygon points="22,6 17,7 19,11" fill="#2962FF" />
  </svg>
);

// Diagonal / trend line — a single straight segment with anchor
// rings at the two endpoints. Blue to match the on-chart rendering.
const DiagonalIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 28 28"
    width="28"
    height="28"
    aria-hidden
  >
    <line
      x1="4"
      y1="24"
      x2="24"
      y2="4"
      stroke="#2962FF"
      strokeWidth="1.5"
    />
    <circle
      cx="4"
      cy="24"
      r="2"
      fill="none"
      stroke="#2962FF"
      strokeWidth="1"
    />
    <circle
      cx="24"
      cy="4"
      r="2"
      fill="none"
      stroke="#2962FF"
      strokeWidth="1"
    />
  </svg>
);

// Long Position — TradingView idiom: two coloured filled zones
// (green TP above entry, red SL below) with a thin entry separator
// drawn as a 1 px filled rectangle. Up-arrow on the right hints
// at direction. Hard-coded green/red because the convention is
// meaningful at-a-glance.
const LongPositionIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 28 28"
    width="28"
    height="28"
    aria-hidden
  >
    <path d="M4 5 h15 v8 h-15 z" fill="#26A69A" fillOpacity="0.55" />
    <path d="M4 15 h15 v8 h-15 z" fill="#F23645" fillOpacity="0.55" />
    <g fill="currentColor" fillRule="nonzero">
      <path d="M4 14 h15 v1 h-15 z" />
      <path d="M22 18 V10 l-3 3 h2 v5 h1 v-5 h2 z" />
    </g>
  </svg>
);

// Short Position — colours flipped, arrow points down.
const ShortPositionIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 28 28"
    width="28"
    height="28"
    aria-hidden
  >
    <path d="M4 5 h15 v8 h-15 z" fill="#F23645" fillOpacity="0.55" />
    <path d="M4 15 h15 v8 h-15 z" fill="#26A69A" fillOpacity="0.55" />
    <g fill="currentColor" fillRule="nonzero">
      <path d="M4 14 h15 v1 h-15 z" />
      <path d="M22 10 V18 l-3 -3 h2 v-5 h1 v5 h2 z" />
    </g>
  </svg>
);

// Price Range — TradingView idiom: a dashed measurement rectangle with a
// vertical double-arrow down its centre (the price move being measured).
const PriceRangeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 28 28"
    width="28"
    height="28"
    aria-hidden
  >
    <rect
      x="4"
      y="5"
      width="20"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      strokeDasharray="3 2"
    />
    <line x1="14" y1="7" x2="14" y2="21" stroke="currentColor" strokeWidth="1.2" />
    <polygon points="14,5 11,9 17,9" fill="currentColor" />
    <polygon points="14,23 11,19 17,19" fill="currentColor" />
  </svg>
);

// Replay — a play triangle inside a clock-ish ring: "run the chart forward
// from a past moment".
const ReplayIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <circle
      cx="10"
      cy="10.6"
      r="6.6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeDasharray="2.6 2.4"
    />
    <path
      d="M8.5 8.4v4.4a.45.45 0 0 0 .69.38l3.4-2.2a.45.45 0 0 0 0-.76l-3.4-2.2a.45.45 0 0 0-.69.38Z"
      fill="currentColor"
    />
  </svg>
);

// Indicators — oscillator-style icon: two horizontal lines (price bars)
// with a wavy sine-like line crossing through (the indicator).
const IndicatorsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 28 28"
    width="28"
    height="28"
    aria-hidden
  >
    {/* Two background price bars */}
    <rect x="4" y="6" width="20" height="1" fill="currentColor" opacity="0.35" />
    <rect x="4" y="22" width="20" height="1" fill="currentColor" opacity="0.35" />
    {/* Wavy indicator line */}
    <polyline
      points="4,20 8,10 12,16 16,8 20,14 24,12"
      stroke="#a78bfa"
      strokeWidth="1.8"
      fill="none"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  </svg>
);

// Trash + Folder use the same filled-path idiom as the line tools.
const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 28 28"
    width="28"
    height="28"
    aria-hidden
  >
    <g fill="currentColor" fillRule="nonzero">
      <path d="M10 4 h8 v1 h-8 z" />
      <path d="M6 7 h16 v1 h-16 z" />
      <path d="M8.5 8 l1 14.5 a1.5 1.5 0 0 0 1.5 1.5 h6 a1.5 1.5 0 0 0 1.5 -1.5 l1 -14.5 h-1 l-1 14.5 a.5 .5 0 0 1 -.5 .5 h-6 a.5 .5 0 0 1 -.5 -.5 l-1 -14.5 z" />
      <path d="M12 11 h1 v9 h-1 z" />
      <path d="M15 11 h1 v9 h-1 z" />
    </g>
  </svg>
);

const FolderIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 28 28"
    width="28"
    height="28"
    aria-hidden
  >
    <g fill="currentColor" fillRule="nonzero">
      <path d="M4 7 h6 l2 2 h12 v14 h-20 z m1 1 v12 h18 v-10 h-11.4 l-2 -2 z" />
    </g>
  </svg>
);

type ToolEntry = {
  value: Exclude<DrawingTool, null>;
  label: string;
  Icon: () => JSX.Element;
};

// Grouped so the toolbar can show a thin separator between conceptual
// clusters (matches TradingView's left rail: line tools → levels →
// forecast/positions).
// Text annotation — serif "T" glyph, matching TradingView's text tool.
const TextIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 28 28"
    width="28"
    height="28"
    aria-hidden
  >
    <g fill="currentColor" fillRule="nonzero">
      {/* top bar */}
      <rect x="6" y="6" width="16" height="2" />
      {/* end serifs hanging off the top bar */}
      <rect x="6" y="6" width="1.6" height="3.4" />
      <rect x="20.4" y="6" width="1.6" height="3.4" />
      {/* stem (centred on x=14) */}
      <rect x="13.2" y="8" width="1.6" height="12.6" />
      {/* base serif */}
      <rect x="10" y="20.6" width="8" height="1.6" />
    </g>
  </svg>
);

const TOOL_GROUPS: ToolEntry[][] = [
  [
    { value: "vertical", label: "Vertical line", Icon: VerticalIcon },
    { value: "horizontal", label: "Horizontal line", Icon: HorizontalIcon },
    { value: "diagonal", label: "Trend line", Icon: DiagonalIcon },
    { value: "path", label: "Elliott wave", Icon: PathIcon },
    { value: "text", label: "Text annotation", Icon: TextIcon },
  ],
  [{ value: "fibonacci", label: "Fibonacci retracement", Icon: FibonacciIcon }],
  [
    { value: "long", label: "Long position", Icon: LongPositionIcon },
    { value: "short", label: "Short position", Icon: ShortPositionIcon },
    { value: "priceRange", label: "Price range", Icon: PriceRangeIcon },
  ],
];

export function Toolbar() {
  const dispatch = useAppDispatch();
  const tabId = useActiveTabId();
  // Read through the focused tab's UI state so toolbar buttons
  // reflect (and target) the pane the user just clicked into.
  const { activeDrawingTool } = useActiveUi();
  const fibDialogOpen = useAppSelector((s) => s.ui.fibLevelsDialogOpen);
  // The right-click menu carries which tool it was opened on so the
  // "Edit settings" item can route to the right dialog.
  const [toolMenu, setToolMenu] = useState<{
    x: number;
    y: number;
    tool: Exclude<DrawingTool, null>;
  } | null>(null);
  // Which registered tool's settings panel is open (null = none). Global Redux
  // state so the left rail, the per-drawing toolbar, AND a drawing's right-click
  // "Settings" all drive the same single panel rendered below.
  const settingsToolId = useAppSelector((s) => s.ui.settingsToolId);
  const menuRef = useRef<HTMLDivElement>(null);
  const [drawingsDialogOpen, setDrawingsDialogOpen] = useState(false);
  const [indicatorsOpen, setIndicatorsOpen] = useState(false);

  // Replay. The button arms a start-bar PICKER rather than jumping straight
  // in: the chart draws a vertical line tracking the cursor and the next
  // click chooses where the replay begins (TradingView's flow). Any active
  // drawing tool is dropped first, so that click can't also place a drawing.
  const { active: replayActive, selecting: replaySelecting } =
    useTabReplay(tabId);
  const hasCandles = useAppSelector(
    (s) => (s.ohlc.byTab[tabId]?.candles.length ?? 0) > 0,
  );

  const toggleReplay = () => {
    if (replayActive) {
      dispatch(exitReplay({ tabId }));
    } else if (replaySelecting) {
      dispatch(cancelReplaySelect({ tabId }));
    } else if (hasCandles) {
      dispatch(setDrawingTool({ tabId, tool: null }));
      dispatch(startReplaySelect({ tabId }));
    }
  };

  // Close the right-click menu on outside click / Esc / scroll.
  useEffect(() => {
    if (!toolMenu) return;
    const onMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setToolMenu(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setToolMenu(null);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [toolMenu]);

  return (
    <div className={styles.toolbox}>
      {TOOL_GROUPS.map((group, groupIdx) => (
        <div key={groupIdx}>
          {groupIdx > 0 ? <div className={styles.groupDivider} /> : null}
          {group.map(({ value, label, Icon }) => {
            // Right-click opens a small context menu whose "Edit
            // settings" item opens the relevant dialog: the levels
            // dialog for Fibonacci, or a LineStyleDialog for the
            // line tools (vertical / horizontal / trend / arrow).
            const hasSettings = toolHasSettings(value);
            return (
              <button
                key={value}
                className={`${styles.btn} ${activeDrawingTool === value ? styles.btnActive : ""}`}
                onClick={() =>
                  dispatch(
                    setDrawingTool({
                      tabId,
                      tool: activeDrawingTool === value ? null : value,
                    }),
                  )
                }
                onContextMenu={
                  hasSettings
                    ? (e) => {
                        e.preventDefault();
                        setToolMenu({
                          x: e.clientX,
                          y: e.clientY,
                          tool: value,
                        });
                      }
                    : undefined
                }
                title={
                  hasSettings
                    ? `${label} (right-click for settings)`
                    : label
                }
                aria-label={label}
              >
                <Icon />
              </button>
            );
          })}
        </div>
      ))}
      <div className={styles.spacer} />
      <div className={styles.groupDivider} />
      <button
        className={`${styles.btn} ${replayActive || replaySelecting ? styles.btnActive : ""}`}
        onClick={toggleReplay}
        disabled={!replayActive && !replaySelecting && !hasCandles}
        title={
          replayActive
            ? "Exit replay"
            : replaySelecting
              ? "Cancel — pick a bar to start from, or Esc"
              : "Replay: pick a bar to start from"
        }
        aria-label="Chart replay"
        aria-pressed={replayActive}
      >
        <ReplayIcon />
      </button>
      <div className={styles.groupDivider} />
      <button
        className={`${styles.btn} ${indicatorsOpen ? styles.btnActive : ""}`}
        onClick={() => setIndicatorsOpen((o) => !o)}
        title="Indicators (MA / RSI)"
        aria-label="Indicators"
      >
        <IndicatorsIcon />
      </button>
      <div className={styles.groupDivider} />
      <button
        className={styles.btn}
        onClick={() => setDrawingsDialogOpen(true)}
        title="Save / load / clear drawing sets"
        aria-label="Drawing sets"
      >
        <FolderIcon />
      </button>
      <button
        className={`${styles.btn} ${styles.btnDanger}`}
        onClick={() => dispatch(clearAllDrawings({ tabId }))}
        title="Clear all drawings"
        aria-label="Clear all drawings"
      >
        <TrashIcon />
      </button>

      {toolMenu && (
        <div
          ref={menuRef}
          className={styles.contextMenu}
          style={{ top: toolMenu.y, left: toolMenu.x }}
          role="menu"
        >
          <button
            type="button"
            className={styles.contextMenuItem}
            onClick={() => {
              const tool = toolMenu.tool;
              setToolMenu(null);
              if (tool === "fibonacci") {
                dispatch(setFibLevelsDialogOpen(true));
              } else if (toolById(tool)?.settings) {
                dispatch(setSettingsToolId(tool));
              }
            }}
            role="menuitem"
          >
            Edit settings
          </button>
        </div>
      )}

      <FibLevelsDialog
        open={fibDialogOpen}
        onClose={() => dispatch(setFibLevelsDialogOpen(false))}
      />

      {/* One generic settings panel for every registered tool (line tools +
          Price Range). Keyed by tool id so each open starts a fresh snapshot. */}
      {settingsToolId && (
        <ToolSettings
          key={settingsToolId}
          toolId={settingsToolId}
          open
          onClose={() => dispatch(setSettingsToolId(null))}
        />
      )}
      <DrawingsDialog
        open={drawingsDialogOpen}
        onClose={() => setDrawingsDialogOpen(false)}
      />
      <IndicatorsPanel
        open={indicatorsOpen}
        onClose={() => setIndicatorsOpen(false)}
      />
      {/* PositionDialog manages its own visibility via ui.positionDialogId,
          so no open/onClose props are required. */}
      <PositionDialog />
    </div>
  );
}
